<template>
  <v-container fluid class="pa-2 org-details">
    <v-card outlined class="px-4 py-3 mb-4">
      <v-row dense>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="organizationDetails.name"
            label="Name"
            hide-details="auto"
            @input="markDetailsChanged"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="organizationDetails.domain"
            label="Domain"
            hide-details="auto"
            @input="markDetailsChanged"
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <logo-upload-field
            v-model="organizationDetails.logo"
            label="Logo url"
            upload-action="OrganizationStore/uploadLogo"
            :owner-id="organizationDetails.id"
            @input="markDetailsChanged"
          ></logo-upload-field>
        </v-col>
        <v-col cols="12" sm="6" md="3">
          <v-text-field
            v-model="organizationDetails.website"
            label="Website"
            title="Where the Property Navigator sends visitors who click this org's logo on the map"
            hide-details="auto"
            @input="markDetailsChanged"
          ></v-text-field>
        </v-col>
        <v-col cols="12" class="d-flex align-center py-0">
          <v-checkbox
            v-model="organizationDetails.hideUnitStatus"
            label="Hide Unit Status"
            hide-details
            @change="markDetailsChanged"
          ></v-checkbox>
        </v-col>
      </v-row>

      <div class="mt-2">
        <div class="caption grey--text text--lighten-1 mb-1">Stylesheet</div>
        <v-textarea
          v-model="organizationDetails.stylesheet"
          outlined
          :rows="showFullStylesheet ? 12 : 1"
          hide-details="auto"
          @input="markDetailsChanged"
        ></v-textarea>
        <v-btn
          text
          x-small
          class="mt-1 grey--text"
          @click="showFullStylesheet = !showFullStylesheet"
        >
          <v-icon x-small left>{{
            showFullStylesheet ? 'mdi-chevron-up' : 'mdi-chevron-down'
          }}</v-icon>
          {{ showFullStylesheet ? 'Show less' : 'Show all' }}
        </v-btn>
      </div>

      <v-divider class="my-3"></v-divider>

      <v-btn
        text
        small
        class="grey--text px-0"
        @click="showMoreDetails = !showMoreDetails"
      >
        <v-icon small left>{{
          showMoreDetails ? 'mdi-chevron-up' : 'mdi-chevron-down'
        }}</v-icon>
        {{ showMoreDetails ? 'Hide details' : 'Show more details' }}
      </v-btn>

      <v-expand-transition>
        <v-row v-if="showMoreDetails" dense class="mt-2">
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="organizationDetails.address1"
              label="Address 1"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="organizationDetails.address2"
              label="Address 2"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="organizationDetails.address3"
              label="Address 3"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="organizationDetails.city"
              label="City"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="organizationDetails.state"
              label="State"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="organizationDetails.postalCode"
              label="Postal code"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="organizationDetails.country"
              label="Country"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="organizationDetails.phone"
              label="Phone"
              hide-details="auto"
              @input="markDetailsChanged"
            ></v-text-field>
          </v-col>
        </v-row>
      </v-expand-transition>

      <v-card-actions class="justify-end px-0 pb-0">
        <v-btn
          small
          text
          :disabled="!detailsChanged"
          @click="discardDetailsChanges"
          >Discard</v-btn
        >
        <v-btn
          small
          color="primary darken-1"
          :disabled="!detailsChanged"
          @click="saveDetails"
          >Save changes</v-btn
        >
      </v-card-actions>
    </v-card>

    <v-toolbar dense>
      <v-tabs v-model="tab" @change="onTabChange">
        <v-tab href="#projects"> Projects </v-tab>
        <v-tab href="#users"> Users </v-tab>
      </v-tabs>
    </v-toolbar>
    <v-tabs-items v-model="currentTab">
      <v-tab-item value="projects" style="margin-top: 10px">
        <div class="d-flex justify-space-between mb-6 bg-surface-variant">
          <v-row>
            <v-col>
              <v-data-table
                :headers="headersProjects"
                :items="
                  this.$store.state.OrganizationStore.projectsForOrganization
                "
                :items-per-page="10"
                class="elevation-1"
                :search="searchProjects"
              >
                <template v-slot:top>
                  <v-row class="upper-elements justify-end">
                    <v-col cols="2" sm="2" md="3">
                      <v-text-field
                        dense
                        v-model="searchProjects"
                        append-icon="mdi-magnify"
                        outlined=""
                        label="Search"
                        hide-details
                        class="mr-0"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                  <tr>
                    {{
                      item.name
                    }}
                  </tr>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                  <v-icon size="24" @click.once="removeProject(item)"
                    >close</v-icon
                  >
                </template>
              </v-data-table>
            </v-col>
            <v-col>
              <v-data-table
                :headers="headersNotAttachedProjects"
                :items="filteredProjects"
                :items-per-page="10"
                @click:row="connectProjectToOrganization"
                class="elevation-1"
                :search="searchNotAttachedprojects"
              >
                <template v-slot:top>
                  <v-row class="upper-elements justify-end">
                    <v-col cols="2" sm="2" md="3">
                      <v-text-field
                        dense
                        v-model="searchNotAttachedprojects"
                        append-icon="mdi-magnify"
                        outlined=""
                        label="Search"
                        hide-details
                        class="mr-0"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                  <tr>
                    {{
                      item.name
                    }}
                  </tr>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>
      <v-tab-item value="users" style="margin-top: 10px">
        <div class="d-flex justify-space-between mb-6 bg-surface-variant">
          <v-row>
            <v-col>
              <v-data-table
                :headers="headersUsers"
                :items="
                  this.$store.state.OrganizationStore.usersForOrganization
                "
                :items-per-page="10"
                class="elevation-1"
                :search="searchUsers"
              >
                <template v-slot:top>
                  <v-row class="upper-elements justify-end">
                    <v-col cols="2" sm="2" md="3">
                      <v-text-field
                        dense
                        v-model="searchUsers"
                        append-icon="mdi-magnify"
                        outlined=""
                        label="Search"
                        hide-details
                        class="mr-0"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                  <tr>
                    {{
                      item.name
                    }}
                  </tr>
                </template>
                <template v-slot:[`item.action`]="{ item }">
                  <v-icon size="24" @click="removeUser(item)">close</v-icon>
                </template>
              </v-data-table>
            </v-col>
            <v-col>
              <v-data-table
                :headers="headersNotAttachedUsers"
                :items="filteredUsers"
                :items-per-page="10"
                @click:row="connectUserToOrganization"
                class="elevation-1"
                :search="searchNotAttachedUsers"
              >
                <template v-slot:top>
                  <v-row class="upper-elements justify-end">
                    <v-col cols="2" sm="2" md="3">
                      <v-text-field
                        dense
                        v-model="searchNotAttachedUsers"
                        append-icon="mdi-magnify"
                        outlined=""
                        label="Search"
                        hide-details
                        class="mr-0"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </template>
                <template v-slot:[`item.name`]="{ item }">
                  <tr>
                    {{
                      item.name
                    }}
                  </tr>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </div>
      </v-tab-item>
    </v-tabs-items>
  </v-container>
</template>

<script>
import LogoUploadField from '../components/LogoUploadField.vue'

export default {
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('OrganizationStore/resetStore')
    next()
  },
  name: 'organizationList',
  props: {
    context: {
      type: String,
      required: false,
      default: 'projects'
    },
    contextUsers: {
      type: String,
      required: false,
      default: 'users'
    }
  },
  components: {
    LogoUploadField
  },
  data: function() {
    return {
      editedIndex: -1,
      organizationDetails: {},
      showFullStylesheet: false,
      showMoreDetails: false,
      detailsChanged: false,
      fetchFilteredUsers: [],
      attachedUsers: [],
      fetchFilteredProjects: [],
      attachedProjects: [],
      tab: 'projects',
      currentTab: 'projects',
      editedItem: {
        id: 0,
        name: ''
      },
      defaultItem: {
        id: 0,
        name: ''
      },
      searchProjects: '',
      searchNotAttachedprojects: '',
      searchUsers: '',
      searchNotAttachedUsers: ''
    }
  },
  computed: {
    headersProjects() {
      return [
        {
          text: 'ATTACHED' + '  ' + this.context.toUpperCase(),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        { text: 'Actions', value: 'action', sortable: false, align: 'right' }
      ]
    },
    headersNotAttachedProjects() {
      return [
        {
          text: this.context.toUpperCase(),
          align: 'left',
          sortable: true,
          value: 'name'
        },
        { text: 'Actions', value: 'action', sortable: false, align: 'right' }
      ]
    },
    headersUsers() {
      return [
        {
          text: 'ATTACHED' + '  ' + this.contextUsers.toUpperCase(),
          align: 'left',
          sortable: true,
          value: 'firstName'
        },
        { text: 'Actions', value: 'action', sortable: false, align: 'right' }
      ]
    },
    headersNotAttachedUsers() {
      return [
        {
          text: this.contextUsers.toUpperCase(),
          align: 'left',
          sortable: true,
          value: 'firstName'
        },
        { text: 'Actions', value: 'action', sortable: false, align: 'right' }
      ]
    },
    filteredUsers() {
      let attachedUsers = this.$store.state.OrganizationStore
        .usersForOrganization
      let fetchFilteredUsers = this.$store.state.UserStore.users
      let users = fetchFilteredUsers.filter(
        e => !attachedUsers.find(({ id }) => e.id === id)
      )
      return users
    },
    filteredProjects() {
      let attachedProjects = this.$store.state.OrganizationStore
        .projectsForOrganization
      let fetchFilteredProjects = this.$store.state.ProjectStore.projects
      let projects = fetchFilteredProjects.filter(
        e => !attachedProjects.find(({ id }) => e.id === id)
      )
      return projects
    }
  },
  async created() {
    await this.$store.dispatch(
      'OrganizationStore/setSelectedOrganization',
      this.$route.params.organizationId
    )
    this.$store.dispatch(
      'OrganizationStore/fetchProjectsForOrganization',
      this.$route.params.organizationId
    )
    this.$store.dispatch(
      'OrganizationStore/fetchUsersForOrganization',
      this.$route.params.organizationId
    )
    this.$store.dispatch('UserStore/fetchUsers')
    this.$store.dispatch('ProjectStore/fetchProjects')

    // A shallow copy, not a direct reference - the fields above are edited
    // in place here, and mutating Vuex state outside a mutation would both
    // violate Vuex convention and make "Discard" impossible without a
    // network round-trip.
    this.organizationDetails = {
      ...this.$store.state.OrganizationStore.selectedOrganization
    }
  },
  methods: {
    onTabChange() {
      this.currentTab = this.tab
    },
    markDetailsChanged() {
      this.detailsChanged = true
    },
    async saveDetails() {
      await this.$store.dispatch(
        'OrganizationStore/updateOrganization',
        this.organizationDetails
      )
      this.detailsChanged = false
    },
    async discardDetailsChanges() {
      await this.$store.dispatch(
        'OrganizationStore/setSelectedOrganization',
        this.$route.params.organizationId
      )
      this.organizationDetails = {
        ...this.$store.state.OrganizationStore.selectedOrganization
      }
      this.detailsChanged = false
    },
    async connectProjectToOrganization(project) {
      await this.$store.dispatch(
        'OrganizationStore/createOrganizationProject',
        {
          projectsId: project.id,
          organizationsId: parseInt(this.$route.params.organizationId)
        }
      )
      this.$store.dispatch(
        'OrganizationStore/fetchProjectsForOrganization',
        parseInt(this.$route.params.organizationId)
      )
    },
    connectUserToOrganization(user) {
      this.$store.dispatch('OrganizationStore/createOrganizationUser', {
        usersId: user.id,
        organizationsId: parseInt(this.$route.params.organizationId)
      })
      this.$store.dispatch(
        'OrganizationStore/fetchUsersForOrganization',
        this.$route.params.organizationId
      )
    },
    removeProject(project) {
      let data = {
        organizationsId: parseInt(this.$route.params.organizationId),
        projectsId: project.id
      }
      this.$store.dispatch('DialogStore/open', {
        title: 'Delete ' + this.context,
        header: false,
        subtitle: 'Are you sure?',
        okText: 'Ok',
        ok: async () => {
          await this.$store.dispatch(
            'OrganizationStore/removeOrganizationProject',
            data
          )
          this.$store.dispatch(
            'OrganizationStore/fetchProjectsForOrganization',
            data.organizationsId
          )
        },
        cancelText: 'Cancel'
      })
    },

    removeUser(user) {
      let data = {
        organizationsId: this.$route.params.organizationId,
        usersId: user.id
      }
      this.$store.dispatch('DialogStore/open', {
        title: 'Delete ' + this.contextUsers,
        header: false,
        subtitle: 'Are you sure?',
        okText: 'Ok',
        ok: () => {
          this.$store.dispatch('OrganizationStore/removeOrganizationUser', data)
          this.$store.dispatch(
            'OrganizationStore/fetchUsersForOrganization',
            this.$route.params.organizationId
          )
        },
        cancelText: 'Cancel'
      })
    },

    close() {
      this.dialog = false
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem)
        this.editedIndex = -1
      }, 300)
    }
  }
}
</script>

<style lang="scss" scoped>
.org-details {
  margin-top: 2vh;
}
.upper-elements {
  margin-bottom: 0px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
}
.v-data-footer {
  margin-bottom: 16px !important;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
}
</style>
